(() => {
  'use strict';

  window.ACReferenceModules = window.ACReferenceModules || {};
  if (window.ACReferenceModules.content) return;
  window.ACReferenceModules.content = true;

  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reducedMotion = () => motion.matches || document.body.dataset.acMotion === 'reduced';
  const bindings = new Map();
  let revealObserver = null;

  const bindRoot = (root, boundKey, setup) => {
    if (!root || root.dataset[boundKey] === 'true') return;
    const controller = new AbortController();
    root.dataset[boundKey] = 'true';
    bindings.set(root, { boundKey, controller });
    setup(controller.signal);
  };

  const bindCart = (root) => {
    bindRoot(root, 'acContentCartBound', (signal) => {
      const status = root.querySelector('[data-ac-content-cart-status]');
      const quantityMessage = root.dataset.acCartQuantityMessage;
      let announceTimer = null;
      const announce = (message) => {
        if (!status) return;
        status.textContent = '';
        window.clearTimeout(announceTimer);
        announceTimer = window.setTimeout(() => { status.textContent = message; }, 20);
      };
      signal.addEventListener('abort', () => window.clearTimeout(announceTimer), { once: true });

      const syncQuantity = (input) => {
        const rule = { min: input.min || 1, max: input.max || null, increment: input.step || 1 };
        const quantity = window.AcProductQuantity?.normalize(input.value, rule) ?? Number(input.value || rule.min);
        input.value = String(quantity);
        const line = input.closest('[data-ac-content-cart-line]');
        const minus = line?.querySelector('[data-ac-content-cart-step="-1"]');
        const plus = line?.querySelector('[data-ac-content-cart-step="1"]');
        if (minus) minus.disabled = quantity <= Number(rule.min);
        if (plus) plus.disabled = rule.max !== null && quantity + Number(rule.increment) > Number(rule.max);
      };

      root.querySelectorAll('[data-ac-content-cart-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const line = button.closest('[data-ac-content-cart-line]');
          const input = line && line.querySelector('[data-ac-content-cart-quantity]');
          if (!input) return;

          const current = Number(input.value || input.min || 1);
          input.value = String(current + Number(button.dataset.acContentCartStep || 0) * Number(input.step || 1));
          input.dispatchEvent(new Event('change', { bubbles: true }));
          if (quantityMessage) announce(quantityMessage);
        }, { signal });
      });

      root.querySelectorAll('[data-ac-content-cart-quantity]').forEach((input) => {
        syncQuantity(input);
        input.addEventListener('change', () => syncQuantity(input), { signal });
      });
    });
  };

  const bindContact = (root) => {
    bindRoot(root, 'acContentContactBound', (signal) => {
      const form = root.querySelector('form');
      const submit = root.querySelector('[data-ac-content-contact-submit]');
      const topic = root.querySelector('.ac-content-contact__topic');
      const topicLabel = topic?.querySelector('.ac-disclosure__summary .ac-action__label');

      const syncSubmitState = () => {
        if (!form || !submit) return;
        submit.setAttribute('aria-disabled', String(!form.checkValidity()));
      };

      if (form) {
        form.addEventListener('input', syncSubmitState, { signal });
        form.addEventListener('change', syncSubmitState, { signal });
        syncSubmitState();
      }

      if (!topic || !topicLabel) return;
      topic.querySelectorAll('[data-ac-content-contact-topic-option]').forEach((option) => {
        option.addEventListener('change', () => {
          if (!option.checked) return;
          topicLabel.textContent = option.value;
        }, { signal });
      });
    });
  };

  const bindReveals = (scope) => {
    const selector = '[data-ac-content-reveal]:not([data-ac-content-reveal-bound])';
    const nodes = [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
    if (!nodes.length) return;
    nodes.forEach((node) => { node.dataset.acContentRevealBound = 'true'; });

    if (reducedMotion() || !window.IntersectionObserver) {
      nodes.forEach((node) => node.classList.add('is-revealed'));
      return;
    }

    revealObserver ||= new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.target.hasAttribute('data-ac-content-reveal-bound')) return;
        if (!entry.isIntersecting && !reducedMotion()) return;
        entry.target.classList.add('is-revealed');
        currentObserver.unobserve(entry.target);
      });
    }, { threshold: 0.12 });
    nodes.forEach((node) => revealObserver.observe(node));
  };

  const destroy = (scope) => {
    for (const [root, binding] of bindings) {
      if (root !== scope && !scope.contains(root)) continue;
      binding.controller.abort();
      delete root.dataset[binding.boundKey];
      bindings.delete(root);
    }
    const nodes = [...(scope.matches?.('[data-ac-content-reveal]') ? [scope] : []), ...scope.querySelectorAll('[data-ac-content-reveal]')];
    nodes.forEach((node) => {
      revealObserver?.unobserve(node);
      delete node.dataset.acContentRevealBound;
      node.classList.remove('is-revealed');
    });
  };

  const init = (scope = document) => {
    const root = scope.matches?.('[data-ac-content-cart]') ? scope : scope.querySelector?.('[data-ac-content-cart]');
    bindCart(root);
    const contact = scope.matches?.('[data-ac-content-contact]') ? scope : scope.querySelector?.('[data-ac-content-contact]');
    bindContact(contact);
    bindReveals(scope);
  };

  const syncMotion = () => {
    if (!reducedMotion()) return;
    document.querySelectorAll('[data-ac-content-reveal-bound]').forEach((node) => {
      node.classList.add('is-revealed');
      revealObserver?.unobserve(node);
    });
  };

  const boot = () => {
    init(document);
    motion.addEventListener('change', syncMotion);
    new MutationObserver(syncMotion).observe(document.body, {
      attributes: true,
      attributeFilter: ['data-ac-motion'],
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('focusin', (event) => {
    const node = event.target.closest('[data-ac-content-reveal]');
    if (!node) return;
    node.classList.add('is-revealed');
    revealObserver?.unobserve(node);
  });
})();
